import React, { useState, useEffect } from 'react';
import api from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { suportaPastaDireta, pastaSalva, prepararPasta, gravarArquivo, esquecerPasta } from '../../utils/pastaBalanca';

/**
 * Exportar para Balança — gera o arquivo de carga de produtos da balança
 * etiquetadora com os produtos marcados "Produto de balança" (backend:
 * GET /produtos/exportar-balanca). O navegador não grava direto na pasta que
 * o programa da balança lê (ex.: C:\Filizola): o arquivo é baixado e salvo
 * lá, e o programa da balança (Smart Editor → Importar) cadastra.
 */
const FORMATOS = [
  { id: 'filizola', label: 'Filizola', pasta: 'C:\\Filizola', arquivo: 'CADTXT.TXT' },
];

export default function ModalExportarBalanca({ onClose }) {
  const [formato, setFormato] = useState('filizola');
  const [gerando, setGerando] = useState(false);
  const [resultado, setResultado] = useState(null);
  const [erro, setErro] = useState('');
  const fmt = FORMATOS.find(f => f.id === formato);
  const { usuario } = useAuth();
  // Pasta da carga configurada pra esta empresa (Parâmetros → PDV → Balança);
  // sem config, vale a padrão do formato.
  const [pastaCfg, setPastaCfg] = useState('');
  const [pastaEscolhida, setPastaEscolhida] = useState(null); // nome da pasta guardada neste computador
  const pasta = pastaCfg || fmt.pasta;
  const direto = suportaPastaDireta();

  useEffect(() => {
    if (usuario?.organizacao_id)
      api.get(`/organizacoes/${usuario.organizacao_id}`)
        .then(({ data }) => setPastaCfg((data.pdv_config?.balanca_pasta_carga || '').trim()))
        .catch(() => {});
    pastaSalva().then(h => setPastaEscolhida(h?.name || null));
  }, [usuario?.organizacao_id]);

  const trocarPasta = async () => {
    await esquecerPasta();
    setPastaEscolhida(null);
  };

  const exportar = async () => {
    setGerando(true); setErro(''); setResultado(null);
    // Pasta resolvida ANTES da chamada de rede (precisa do clique "fresco").
    let handle = null;
    if (direto) {
      try { handle = await prepararPasta(); setPastaEscolhida(handle.name); }
      catch (e) { if (e?.name === 'AbortError') { setGerando(false); return; } handle = null; }
    }
    try {
      const { data } = await api.get('/produtos/exportar-balanca', { params: { formato } });
      setResultado(data);
      if (!data.total) return;
      if (handle) {
        await gravarArquivo(handle, data.arquivo, data.conteudo);
        setResultado({ ...data, gravadoEm: handle.name });
        return;
      }
      // Arquivo texto puro (sem BOM): o programa da balança lê byte a byte.
      const blob = new Blob([data.conteudo], { type: 'text/plain;charset=us-ascii' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = data.arquivo;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      setErro(err.response?.data?.erro || 'Erro ao gerar o arquivo da balança.');
    } finally { setGerando(false); }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex',
      alignItems: 'center', justifyContent: 'center', zIndex: 2000 }}
      onMouseDown={e => e.target === e.currentTarget && onClose()}>
      <div style={{ background: 'var(--card-bg, #fff)', borderRadius: 10, boxShadow: '0 10px 30px rgba(0,0,0,0.25)',
        width: 520, maxWidth: '92vw', maxHeight: '85vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <div style={{ background: '#0a2540', color: '#fff', padding: '14px 20px', fontWeight: 700, fontSize: 14.5 }}>
          ⚖️ Exportar para Balança
        </div>
        <div style={{ padding: 18, overflowY: 'auto', flex: 1, fontSize: 13 }}>
          <strong style={{ display: 'block', marginBottom: 6 }}>Formato</strong>
          <div style={{ display: 'flex', gap: 16, marginBottom: 14 }}>
            {FORMATOS.map(f => (
              <label key={f.id} style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
                <input type="radio" name="formato-balanca" checked={formato === f.id} onChange={() => setFormato(f.id)} />
                {f.label}
              </label>
            ))}
          </div>
          <p style={{ fontSize: 12.5, color: '#444', lineHeight: 1.5, margin: '0 0 12px' }}>
            Vão no arquivo os produtos ativos marcados como <strong>Produto de balança</strong> (Cad. Produtos →
            Atributos), com código interno, descrição, preço de venda e tipo (peso para unidade que fraciona,
            como KG; unidade para as demais). O <strong>{fmt.arquivo}</strong> vai para a pasta{' '}
            <strong>{pasta}</strong> (a mesma configurada no programa da balança); depois use o
            Importar do programa da balança para cadastrar.
          </p>
          {direto ? (
            <p style={{ fontSize: 12.5, lineHeight: 1.5, margin: '0 0 12px', padding: '8px 10px', borderRadius: 6,
              background: pastaEscolhida ? '#ecfdf5' : '#fff8e1', border: `1px solid ${pastaEscolhida ? '#a7f3d0' : '#ffe082'}` }}>
              {pastaEscolhida ? (
                <>📁 Neste computador grava direto na pasta <strong>{pastaEscolhida}</strong> (substitui o arquivo anterior).{' '}
                  <button type="button" onClick={trocarPasta} style={{ ...btnStyle(), padding: '2px 8px', fontSize: 11.5 }}>Trocar pasta</button></>
              ) : (
                <>📁 1ª vez neste computador: ao exportar, escolha a pasta <strong>{pasta}</strong> na janela que abrir
                  e clique em "Permitir". Nas próximas vezes o arquivo vai direto pra lá.</>
              )}
            </p>
          ) : (
            <p style={{ fontSize: 12, color: '#92400e', margin: '0 0 12px' }}>
              Este navegador não grava direto em pasta: o arquivo vai para Downloads; mova para {pasta}.
              (No Edge ou Chrome ele grava direto.)
            </p>
          )}

          {erro && <p style={{ color: '#b91c1c', margin: '0 0 10px' }}>{erro}</p>}
          {resultado && (
            <div style={{ border: '1px solid #e5e7eb', borderRadius: 6, padding: 10 }}>
              <p style={{ margin: 0 }}>
                {resultado.total
                  ? <>✔ <strong>{resultado.total}</strong> produto(s) no arquivo <strong>{resultado.arquivo}</strong>
                      {resultado.gravadoEm ? <> — gravado na pasta <strong>{resultado.gravadoEm}</strong>.</> : ' (baixado).'}</>
                  : 'Nenhum produto de balança para exportar.'}
              </p>
              {resultado.ignorados?.length > 0 && (
                <>
                  <p style={{ margin: '8px 0 4px', color: '#92400e' }}>
                    {resultado.ignorados.length} produto(s) ficaram de fora:
                  </p>
                  <ul style={{ margin: 0, paddingLeft: 18, maxHeight: 160, overflowY: 'auto', fontSize: 12 }}>
                    {resultado.ignorados.map((i, n) => (
                      <li key={n}>{i.codigo || '(sem código)'} — {i.nome}: {i.motivo}</li>
                    ))}
                  </ul>
                </>
              )}
            </div>
          )}
        </div>
        <div style={{ padding: '12px 20px', borderTop: '1px solid #e5e7eb', display: 'flex',
          justifyContent: 'flex-end', gap: 8 }}>
          <button onClick={exportar} disabled={gerando} style={btnStyle(true)}>
            {gerando ? 'Gerando…' : '⬇️ Exportar'}
          </button>
          <button onClick={onClose} style={btnStyle()}>Fechar</button>
        </div>
      </div>
    </div>
  );
}

const btnStyle = (primario) => ({
  padding: '7px 14px', borderRadius: 6, fontSize: 12.5, cursor: 'pointer',
  border: primario ? 'none' : '1px solid #d1d5db',
  background: primario ? '#1565FF' : '#fff',
  color: primario ? '#fff' : '#333',
  fontWeight: primario ? 700 : 500,
});
